import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileSpreadsheet, Calendar, Printer, FileDown } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const fmtAmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ReportPage({ title, subtitle, endpoint, columns, totalsBuilder, testid }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!from || !to) { toast.error("Select both From and To dates"); return; }
    setBusy(true);
    try {
      const { data } = await api.get(endpoint, { params: { from_date: from, to_date: to } });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load report"); }
    finally { setBusy(false); }
  };

  const exportCsv = () => {
    if (!data) return;
    const head = columns.map(c => c.label);
    const lines = data.rows.map(r => columns.map(c => c.export ? c.export(r) : r[c.key] ?? ""));
    const csv = [head, ...lines].map(cols => cols.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${testid}_${from}_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const totals = data ? totalsBuilder(data.totals, data.rows) : null;
  const hasData = data && data.rows && data.rows.length > 0;
  const pg = usePagination(data?.rows || [], data);

  return (
    <div className="p-8" data-testid={`${testid}-page`}>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4 print:hidden">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><FileSpreadsheet className="w-3 h-3"/> Reports</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter" data-testid="report-title">{title}</h1>
          {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">From</Label>
            <Input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">To</Label>
            <Input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/>
          </div>
          <Button data-testid="report-apply" onClick={generate} disabled={busy}
            className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider">
            <Calendar className="w-3 h-3 mr-1"/> {busy ? "Generating…" : "Generate Report"}
          </Button>
          <Button data-testid="report-print" variant="outline" disabled={!hasData} onClick={() => window.print()}
            className="rounded-sm text-xs uppercase tracking-wider">
            <Printer className="w-3 h-3 mr-1"/> Print
          </Button>
          <Button data-testid="report-export" variant="outline" disabled={!hasData} onClick={exportCsv}
            className="rounded-sm text-xs uppercase tracking-wider">
            <FileDown className="w-3 h-3 mr-1"/> Export
          </Button>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>{columns.map(c => <th key={c.key} className={`p-3 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {!data && (
              <tr><td colSpan={columns.length} className="p-10 text-center text-muted-foreground text-xs uppercase tracking-widest" data-testid="report-empty">
                Select a date range and click Generate Report
              </td></tr>
            )}
            {data && !hasData && (
              <tr><td colSpan={columns.length} className="p-10 text-center text-muted-foreground text-xs uppercase tracking-widest" data-testid="report-no-data">
                No records found for the selected date range
              </td></tr>
            )}
            {hasData && pg.pageItems.map((r, i) => (
              <tr key={i} data-testid={`report-row-${i}`} className="border-t border-border">
                {columns.map(c => (
                  <td key={c.key} className={`p-3 text-xs ${c.align === "right" ? "text-right font-mono" : ""} ${c.className || ""}`}>
                    {c.render ? c.render(r) : r[c.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
            {hasData && totals && (
              <tr className="border-t-2 border-primary bg-secondary/40 font-bold" data-testid="report-totals">
                {totals.map((cell, i) => (
                  <td key={i} className={`p-3 text-xs ${cell.align === "right" ? "text-right font-mono" : ""} ${cell.className || "uppercase tracking-widest"}`}>
                    {cell.value}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}

export function SalesReport() {
  return <ReportPage title="Sales Reports" subtitle="Retail & wholesale sales within selected date range"
    endpoint="/reports/sales-list" testid="sales-report"
    columns={[
      { key: "date", label: "Date" },
      { key: "invoice_no", label: "Invoice No" },
      { key: "party", label: "Customer" },
      { key: "type", label: "Type" },
      { key: "qty", label: "Qty", align: "right" },
      { key: "subtotal", label: "Subtotal", align: "right", render: (r) => fmtAmt(r.subtotal), export: (r) => r.subtotal },
      { key: "tax", label: "Tax", align: "right", render: (r) => fmtAmt(r.tax), export: (r) => r.tax },
      { key: "total", label: "Total", align: "right", render: (r) => fmtAmt(r.total), export: (r) => r.total },
    ]}
    totalsBuilder={(t) => [
      { value: "TOTAL" }, { value: "" }, { value: "" }, { value: "" },
      { value: fmtAmt(t.qty), align: "right" },
      { value: fmtAmt(t.subtotal), align: "right" },
      { value: fmtAmt(t.tax), align: "right" },
      { value: fmtAmt(t.total), align: "right" },
    ]}/>;
}

export function PurchaseReport() {
  return <ReportPage title="Purchase Reports" subtitle="Supplier purchases within selected date range"
    endpoint="/reports/purchase-list" testid="purchase-report"
    columns={[
      { key: "date", label: "Date" },
      { key: "bill_no", label: "Bill No" },
      { key: "supplier", label: "Supplier" },
      { key: "type", label: "Type" },
      { key: "qty", label: "Qty", align: "right" },
      { key: "subtotal", label: "Subtotal", align: "right", render: (r) => fmtAmt(r.subtotal), export: (r) => r.subtotal },
      { key: "tax", label: "Tax", align: "right", render: (r) => fmtAmt(r.tax), export: (r) => r.tax },
      { key: "total", label: "Total", align: "right", render: (r) => fmtAmt(r.total), export: (r) => r.total },
    ]}
    totalsBuilder={(t) => [
      { value: "TOTAL" }, { value: "" }, { value: "" }, { value: "" },
      { value: fmtAmt(t.qty), align: "right" },
      { value: fmtAmt(t.subtotal), align: "right" },
      { value: fmtAmt(t.tax), align: "right" },
      { value: fmtAmt(t.total), align: "right" },
    ]}/>;
}

export function StockReport() {
  return <ReportPage title="Stock Reports" subtitle="Opening, inward, outward, closing & valuation"
    endpoint="/reports/stock-movement" testid="stock-report"
    columns={[
      { key: "item_name", label: "Item" },
      { key: "part_no", label: "Part No" },
      { key: "unit", label: "Unit" },
      { key: "opening", label: "Opening", align: "right" },
      { key: "in", label: "Inward", align: "right" },
      { key: "out", label: "Outward", align: "right" },
      { key: "closing", label: "Closing", align: "right" },
      { key: "value", label: "Value", align: "right", render: (r) => fmtAmt(r.value), export: (r) => r.value },
    ]}
    totalsBuilder={(t) => [
      { value: "TOTAL" }, { value: "" }, { value: "" }, { value: "" },
      { value: fmtAmt(t.in), align: "right" },
      { value: fmtAmt(t.out), align: "right" },
      { value: "", align: "right" },
      { value: fmtAmt(t.value), align: "right" },
    ]}/>;
}

export function WorkshopReport() {
  return <ReportPage title="Workshop Reports" subtitle="Job cards, mechanics & service revenue"
    endpoint="/reports/workshop-list" testid="workshop-report"
    columns={[
      { key: "date", label: "Date" },
      { key: "job_no", label: "Job No" },
      { key: "customer", label: "Customer" },
      { key: "vehicle", label: "Vehicle" },
      { key: "mechanic", label: "Mechanic" },
      { key: "job_type", label: "Job Type" },
      { key: "status", label: "Status" },
      { key: "parts_total", label: "Parts", align: "right", render: (r) => fmtAmt(r.parts_total), export: (r) => r.parts_total },
      { key: "labour_total", label: "Labour", align: "right", render: (r) => fmtAmt(r.labour_total), export: (r) => r.labour_total },
      { key: "total", label: "Total", align: "right", render: (r) => fmtAmt(r.total), export: (r) => r.total },
    ]}
    totalsBuilder={(t) => [
      { value: "TOTAL" }, { value: "" }, { value: "" }, { value: "" }, { value: "" }, { value: "" }, { value: "" },
      { value: fmtAmt(t.parts_total), align: "right" },
      { value: fmtAmt(t.labour_total), align: "right" },
      { value: fmtAmt(t.total), align: "right" },
    ]}/>;
}

export function FinancialReport() {
  return <ReportPage title="Financial Reports" subtitle="Revenue, cost of goods, expenses & profitability"
    endpoint="/reports/financial-list" testid="financial-report"
    columns={[
      { key: "category", label: "Category" },
      { key: "particular", label: "Particulars" },
      { key: "credit", label: "Credit", align: "right", render: (r) => r.credit ? fmtAmt(r.credit) : "—", export: (r) => r.credit },
      { key: "debit", label: "Debit", align: "right", render: (r) => r.debit ? fmtAmt(r.debit) : "—", export: (r) => r.debit },
    ]}
    totalsBuilder={(t) => [
      { value: "TOTAL" },
      { value: `Gross ₹${fmtAmt(t.gross_profit)} · Net ₹${fmtAmt(t.net_profit)}`, className: "text-primary" },
      { value: fmtAmt(t.credit), align: "right" },
      { value: fmtAmt(t.debit), align: "right" },
    ]}/>;
}
